import React from 'react';
import { Card, BlockStack, InlineStack, Text, Badge, Button, Divider } from '@shopify/polaris';
import { SUGGESTION_SEVERITY_TONES, CATEGORY_LABELS } from '../theme';
import { StaggeredList } from './ui';

/**
 * T056: SuggestionList component.
 * Ordered cards with severity badge, title, detail, action, estimated impact.
 * Uses theme config for severity and category mapping; staggered entrance.
 *
 * @see documents/ui-enhancement-spec.md §10, §9.3
 */

interface Suggestion {
  id: string;
  category: string;
  severity: string;
  affectedField: string;
  title: string;
  detail: string;
  recommendedAction: string;
  estimatedImpact: number;
  isActioned: boolean;
  actionedAt: string | null;
  isBlurred: boolean;
}

interface SuggestionListProps {
  suggestions: Suggestion[];
  onAction?: (suggestionId: string) => void;
  isActioning?: boolean;
}

function severityToBadgeTone(severity: string): 'critical' | 'warning' | 'info' | 'success' {
  return SUGGESTION_SEVERITY_TONES[severity] ?? 'info';
}

function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

export function SuggestionList({ suggestions, onAction, isActioning }: SuggestionListProps): React.ReactElement {
  const visibleSuggestions = suggestions.filter((s) => !s.isBlurred);

  if (visibleSuggestions.length === 0) {
    return (
      <Card>
        <BlockStack gap="300" inlineAlign="center">
          <Text as="p" variant="bodyMd" alignment="center">
            No suggestions available.
          </Text>
        </BlockStack>
      </Card>
    );
  }

  return (
    <BlockStack gap="300">
      <StaggeredList>
        {visibleSuggestions.map((suggestion) => (
        <Card key={suggestion.id}>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <InlineStack gap="200">
                <Badge tone={severityToBadgeTone(suggestion.severity)}>
                  {suggestion.severity}
                </Badge>
                <Text as="span" variant="bodySm" tone="subdued">
                  {categoryLabel(suggestion.category)}
                </Text>
              </InlineStack>
              {suggestion.estimatedImpact > 0 && (
                <Badge tone="success">{`+${suggestion.estimatedImpact} points`}</Badge>
              )}
            </InlineStack>

            <Text as="h3" variant="headingSm">
              {suggestion.title}
            </Text>

            <Text as="p" variant="bodySm">
              {suggestion.detail}
            </Text>

            <Divider />

            <InlineStack align="space-between" blockAlign="center">
              <Text as="p" variant="bodySm" fontWeight="medium">
                {suggestion.recommendedAction}
              </Text>
              {suggestion.isActioned ? (
                <Badge tone="success">Done</Badge>
              ) : (
                onAction && (
                  <Button
                    size="slim"
                    onClick={() => onAction(suggestion.id)}
                    loading={isActioning}
                  >
                    Mark as done
                  </Button>
                )
              )}
            </InlineStack>
          </BlockStack>
        </Card>
      ))}
      </StaggeredList>
    </BlockStack>
  );
}
